import * as React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';
import { type TestIdProps, testIdProps } from '../../lib/test-id';
import { Button } from '../atoms/button';

export interface PaginationProps extends React.HTMLAttributes<HTMLElement>, TestIdProps {
  page: number;
  pageCount: number;
  onPageChange?: (page: number) => void;
  variant?: 'numbered' | 'simple' | 'compact';
  siblingCount?: number;
}

function range(start: number, end: number) {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function getPages(page: number, pageCount: number, siblingCount: number) {
  const totalNumbers = siblingCount * 2 + 5;
  if (pageCount <= totalNumbers) return range(1, pageCount);

  const left = Math.max(page - siblingCount, 1);
  const right = Math.min(page + siblingCount, pageCount);
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < pageCount - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    return [...range(1, 3 + siblingCount * 2), 'ellipsis', pageCount] as const;
  }
  if (showLeftEllipsis && !showRightEllipsis) {
    return [1, 'ellipsis', ...range(pageCount - (2 + siblingCount * 2), pageCount)] as const;
  }
  return [1, 'ellipsis', ...range(left, right), 'ellipsis', pageCount] as const;
}

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  (
    {
      className,
      page,
      pageCount,
      onPageChange,
      variant = 'numbered',
      siblingCount = 1,
      testId,
      ...props
    },
    ref,
  ) => {
    const canPrev = page > 1;
    const canNext = page < pageCount;

    if (variant === 'simple') {
      return (
        <nav
          ref={ref}
          aria-label="pagination"
          className={cn('flex items-center justify-between gap-4', className)}
          {...props}
          {...testIdProps(testId)}
        >
          <Button
            variant="default"
            size="xs"
            disabled={!canPrev}
            onClick={() => onPageChange?.(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {pageCount}
          </span>
          <Button
            variant="default"
            size="xs"
            disabled={!canNext}
            onClick={() => onPageChange?.(page + 1)}
          >
            Next
          </Button>
        </nav>
      );
    }

    if (variant === 'compact') {
      return (
        <nav
          ref={ref}
          aria-label="pagination"
          className={cn('inline-flex items-center gap-1', className)}
          {...props}
          {...testIdProps(testId)}
        >
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            disabled={!canPrev}
            aria-label="Previous page"
            onClick={() => onPageChange?.(page - 1)}
          >
            <ChevronLeft />
          </Button>
          <span className="min-w-16 text-center text-sm text-muted-foreground">
            {page} / {pageCount}
          </span>
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            disabled={!canNext}
            aria-label="Next page"
            onClick={() => onPageChange?.(page + 1)}
          >
            <ChevronRight />
          </Button>
        </nav>
      );
    }

    const pages = getPages(page, pageCount, siblingCount);

    return (
      <nav
        ref={ref}
        aria-label="pagination"
        className={cn('flex items-center gap-1', className)}
        {...props}
        {...testIdProps(testId)}
      >
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          disabled={!canPrev}
          aria-label="Previous page"
          onClick={() => onPageChange?.(page - 1)}
        >
          <ChevronLeft />
        </Button>
        {pages.map((p, i) =>
          p === 'ellipsis' ? (
            <span
              key={`e-${i}`}
              className="flex size-control-tiny items-center justify-center text-quaternary"
            >
              <MoreHorizontal className="size-4" />
            </span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'primary' : 'ghost'}
              size="xs"
              aria-current={p === page ? 'page' : undefined}
              onClick={() => onPageChange?.(p as number)}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          disabled={!canNext}
          aria-label="Next page"
          onClick={() => onPageChange?.(page + 1)}
        >
          <ChevronRight />
        </Button>
      </nav>
    );
  },
);
Pagination.displayName = 'Pagination';

export { Pagination };
